import React from "react";
import {
  Box,
  HStack,
  SimpleGrid,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { Chart } from "react-google-charts";
import InsightsSection from "./InsightsSection";
import PlaceholderTile from "./PlaceholderTile";

const TradeReviewSection = ({
  setupTypePerformance = [],
  planAdherence = {},
  reviewCoverage = {},
  currencySymbol = "$",
}) => {
  const { colorMode } = useColorMode();
  const cardBg = colorMode === "light" ? "gray.50" : "gray.600";
  const labelColor = colorMode === "light" ? "gray.600" : "gray.300";

  const hasSetup = Array.isArray(setupTypePerformance) && setupTypePerformance.length > 0;
  const hasPlan = planAdherence && Object.keys(planAdherence).length > 0;
  const hasCoverage = reviewCoverage && Object.keys(reviewCoverage).length > 0;

  const followed = planAdherence?.followed || {};
  const not_followed = planAdherence?.not_followed || {};

  const chartData = hasSetup
    ? [
        ["Setup", "Win Rate %", { role: "style" }],
        ...setupTypePerformance.map((s) => [
          s.setup_type || "Unspecified",
          s.win_pct ?? 0,
          (s.win_pct ?? 0) >= 50 ? "#48BB78" : "#FC8181",
        ]),
      ]
    : null;

  const chartOptions = {
    chartArea: { width: "70%", height: "80%" },
    backgroundColor: colorMode === "light" ? "#FDFDFD" : "#1A202C",
    legend: "none",
    hAxis: {
      title: "Win Rate %",
      titleTextStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
      textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" },
    },
    vAxis: { textStyle: { color: colorMode === "light" ? "#636363" : "#E2E8F0" } },
  };

  return (
    <InsightsSection
      title="Trade Review Stats"
      description="Setup performance, plan adherence, and review coverage from your trade reviews."
    >
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Plan-Followed Win %</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasPlan && followed.win_pct != null ? `${followed.win_pct}%` : "—"}
          </Text>
          <Text fontSize="xs" color={labelColor}>
            {hasPlan ? `n=${followed.num_trades ?? 0}` : ""}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Plan-Not-Followed Win %</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasPlan && not_followed.win_pct != null ? `${not_followed.win_pct}%` : "—"}
          </Text>
          <Text fontSize="xs" color={labelColor}>
            {hasPlan ? `n=${not_followed.num_trades ?? 0}` : ""}
          </Text>
        </Box>
        <Box bg={cardBg} rounded="md" p={3}>
          <Text fontSize="xs" color={labelColor}>Review Coverage</Text>
          <Text fontSize="lg" fontWeight="bold">
            {hasCoverage && reviewCoverage.coverage_pct != null
              ? `${reviewCoverage.coverage_pct}%`
              : "—"}
          </Text>
          <Text fontSize="xs" color={labelColor}>
            {hasCoverage && reviewCoverage.avg_days_to_review != null
              ? `Avg ${reviewCoverage.avg_days_to_review}d to review`
              : ""}
          </Text>
        </Box>
      </SimpleGrid>

      {hasSetup ? (
        <Box bg={colorMode === "light" ? "white" : "gray.700"} rounded="md" h="280px">
          <Chart
            chartType="BarChart"
            width="100%"
            height="100%"
            data={chartData}
            options={chartOptions}
          />
        </Box>
      ) : (
        <PlaceholderTile message="No setup-type data yet — fill in setup_type on a trade review to see a breakdown." />
      )}

      {hasSetup && (
        <HStack spacing={6} mt={3} flexWrap="wrap">
          {setupTypePerformance.slice(0, 6).map((s) => (
            <Box key={s.setup_type || "unspecified"} fontSize="xs" color={labelColor}>
              <Text as="span" fontWeight="bold">{s.setup_type || "Unspecified"}: </Text>
              {currencySymbol}{Number(s.total_pnl || 0).toFixed(2)} · {s.num_trades} trades
            </Box>
          ))}
        </HStack>
      )}
    </InsightsSection>
  );
};

export default TradeReviewSection;
